/**
 * App frame (DESIGN §3): a left rail on desktop, a bottom tab bar under 900px.
 * Tabs by role (owner's ask: "tabs that are easy to use; managers need a different tab"):
 *   staff:              Today · Checks · Report · Learn · Profile
 *   managers, owners:   Today · Team · Checks · Report · More
 * "Today" is what to do now; "Profile" is you (permits, lessons, settings, sign out). Owner, 2026-09-28: "For You" and
 * "Me" read as the same thing, so the two words must never collide. Routes stay / and /me so old links still work.
 * The desktop rail shows the same tabs in the same order with the same names, then the rest under "More".
 */
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { BillingPrompts } from "./Billing.tsx";
import { PhotoPlanHost } from "./PhotoPlanHost.tsx";
import { HeldEntries } from "./HeldEntries.tsx";
import { DemoGate } from "./DemoAsk.tsx";
import { demoSiteUrl, isDemoSample } from "../lib/demoAsk.ts";
import { freeTrialUrl } from "../lib/demoTour.ts";
import { HEADER_CTA } from "../lib/demoCta.ts";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { REFERRAL_TITLE, type Role, type Switch } from "@logwave/core";
import { OwnershipOfferBanner } from "./Ownership.tsx";
import { useAuth } from "../auth.tsx";
import { config } from "../env.ts";
import { useOnline } from "../lib/useLive.ts";
import { ROLE_LABEL } from "../lib/labels.ts";
import { useApi, useSession } from "../session.tsx";
import { refreshWebPush, webPushSupport } from "../lib/push.ts";
import { CleaverMark } from "./Brand.tsx";
import { ReferralCelebration } from "./Referrals.tsx";
import { Icon, type IconName } from "./Icons.tsx";
import { Button, LinkButton, Page, Skeleton } from "./ui.tsx";
import { AskButton } from "./AskButton.tsx";
import { lazyInline } from "../lib/lazy.ts";
const SimpleStepsCard = lazyInline(() => import("./EasySteps.tsx"), "SimpleStepsCard");
import { isHomeOrg } from "../api.ts";
import { ScreenBoundary, screenName } from "./ScreenError.tsx";
import { AppEventsBridge } from "./AppEventsBridge.tsx";
import { UsageDataCard } from "./UsageData.tsx";
import { OutboxBanner } from "./OutboxBanner.tsx";
import { roleBlockedText } from "../lib/failures.ts";
import { useScrollRestore } from "../lib/scrollRestore.ts";
const InYourHands = lazyInline(() => import("./InYourHands.tsx"), "InYourHands");
// App search (core appsearch.ts): its own chunk, loaded the first time it opens.
const AppSearch = lazyInline(() => import("./AppSearch.tsx"), "AppSearch");
import { recordUse } from "../lib/searchUsage.ts";
import { PeekHost } from "./Peek.tsx";

/** `sw`: also needs this switch from the owner (core ownership.ts; Billing also opens for whoever sees the cost). */
export interface NavItem { to: string; label: string; min: Role; icon: IconName;  group?: MoreGroup; sw?: Switch }
/** Shown to this person: their role reaches it, and the owner shares its switch (owners always). */
export const navShows = (s: { can(min: Role): boolean; may(sw: Switch): boolean; seesCost: boolean }, n: Pick<NavItem, "min" | "sw">): boolean =>
  s.can(n.min) && (!n.sw || s.may(n.sw) || (n.sw === "billing" && s.seesCost));
/** The More list's headings (docs/ux/UX-WRITING-AUDIT.md 5b): an owner scans four lines, not twenty. */
/**
 * Heading order = how often a manager opens each in a shift; the account group is LAST, just above the person's name.
 * Owner, 2026-10-09: "The ordering of things on the side bar seems off" and "Account is normally at bottom".
 */
export const MORE_GROUPS = ["Today", "Proof for the inspector", "Team and learning", "How it's going", "Grow", "Your account"] as const;
export type MoreGroup = (typeof MORE_GROUPS)[number];

const T = (to: string, label: string, icon: IconName, min: Role = "staff"): NavItem => ({ to, label, icon, min });
export const STAFF_TABS: NavItem[] = [
  T("/", "Today", "spark"), T("/do", "Checks", "do"), T("/report", "Report", "report"),
  T("/learn", "Learn", "learn"), T("/me", "Profile", "people"),
];
export const MANAGER_TABS: NavItem[] = [
  T("/", "Today", "spark"), T("/team", "Team", "people", "manager"), T("/do", "Checks", "do"),
  T("/report", "Report", "report"), T("/more", "More", "more"),
];
/**
 * Everything that isn't a tab (the More page, and the rail under "More"). Staff see only what their job uses
 * (ROLE_ONTOLOGY food worker: label the food, clean the place, use the machine, find the soap): Food, Cleaning, Equipment,
 * Supplies. Ordering, paper sheets, event kits, workspaces and the business-referral ask are for managers and owners:
 * an hourly worker has no use for them, and a referral ask to them serves the app, not their good (B = MAT, G2b).
 */
export const MORE: NavItem[] = [
  { to: "/today", label: "Everything today", min: "manager", icon: "today", group: "Today" },
  { to: "/food", label: "Food: what's in the fridge, and when it goes", min: "staff", icon: "fridge", group: "Today" },
  { to: "/places", label: "Cleaning: what to clean, and where", min: "staff", icon: "pin", group: "Today" },
  { to: "/objects", label: "Equipment: fridges, thermometers and machines, and how to use each", min: "staff", icon: "wrench", group: "Today" },
  { to: "/supplies", label: "Supplies: what you have, and where it's kept", min: "staff", icon: "box", group: "Today" },
  { to: "/walk", label: "Walk the rooms: what's in place, and what to add", min: "manager", icon: "camera", group: "Today" },
  { to: "/order", label: "Order list: what's low, by supplier", min: "manager", icon: "cart", group: "Today" },
  { to: "/prove", label: "Inspection binder: one PDF, the way an inspector reads it", min: "manager", icon: "prove", group: "Proof for the inspector" },
  { to: "/audit-trail", label: "Audit trail: who did what, and when", min: "manager", icon: "book", group: "Proof for the inspector" },
  { to: "/signs", label: "Signs and forms: what to post and keep", min: "manager", icon: "sign", group: "Proof for the inspector" },
  { to: "/paper", label: "Paper logs: print sheets, snap photos", min: "manager", icon: "camera", group: "Proof for the inspector" },
  { to: "/open", label: "Open legally: permits and checklist", min: "admin", icon: "sign", group: "Proof for the inspector" },
  { to: "/people", label: "People: your team, invites, roles, and how to reach them", min: "manager", icon: "people", group: "Team and learning" },
  { to: "/learn", label: "Learn: the state course, lessons and library", min: "manager", icon: "learn", group: "Team and learning" },
  { to: "/kits", label: "Event kits: what to pack for a booth", min: "manager", icon: "box", group: "Team and learning" },
  { to: "/insights", label: "Busy hours: patterns from your records", min: "manager", icon: "spark", group: "How it's going" },
  { to: "/invite-business", label: REFERRAL_TITLE, min: "manager", icon: "text", group: "Grow" },
  notifyNav(),
  { to: "/messages", label: "Messages and support", min: "manager", icon: "bell", group: "Your account" },
  { to: "/billing", label: "Plan and billing: who you pay for, your card and invoices", min: "manager", sw: "billing", icon: "card", group: "Your account" },
  { to: "/settings", label: "Settings", min: "manager", sw: "settings", icon: "settings", group: "Your account" },
  { to: "/privacy", label: "Your data and privacy: download, choices, delete", min: "manager", icon: "eye", group: "Your account" },
  { to: "/workspaces", label: "Switch business or home", min: "manager", icon: "handoff", group: "Your account" },
];
/** The More items in heading order, each heading with its items (a heading with none for this role is dropped). */
export function moreGrouped(items: NavItem[]): { group: string; items: NavItem[] }[] {
  const g = MORE_GROUPS.map((group) => ({ group: group as string, items: items.filter((n) => n.group === group) }));
  const rest = items.filter((n) => !n.group);
  return [...g, ...(rest.length ? [{ group: "", items: rest }] : [])].filter((x) => x.items.length > 0);
}
/** A home (docs/HOME.md): no roles, no legal logs. Home · Food · Rooms · Have · More. */
export const HOME_TABS: NavItem[] = [
  T("/", "Home", "spark"), T("/food", "Food", "fridge"), T("/rooms", "Rooms", "book"),
  T("/have", "Have", "cart"), T("/more", "More", "more"),
];
export const HOME_MORE: NavItem[] = [
  { to: "/have?view=shop", label: "Shopping list", min: "staff", icon: "cart", group: "Today" },
  { to: "/rooms/where", label: "Where should this go?", min: "staff", icon: "search", group: "Today" },
  { to: "/objects", label: "Equipment: fridges, thermometers and machines, and how to use each", min: "staff", icon: "wrench", group: "Today" },
  { to: "/invite-business", label: REFERRAL_TITLE, min: "staff", icon: "text", group: "Grow" },
  notifyNav(),
  { to: "/messages", label: "Messages and support", min: "staff", icon: "bell", group: "Your account" },
  { to: "/billing", label: "Plan and billing: your card and invoices", min: "owner", icon: "card", group: "Your account" },
  { to: "/workspaces", label: "Switch business or home", min: "staff", icon: "handoff", group: "Your account" },
];
/**
 * Notifications in every role's More (owner, 2026-10-11), businesses and homes. One object for both lists. Literal label: the label
 * lint reads this file, and importing core notifyprefs here would pull its catalog into the main chunk (test/notify-surface-real
 * checks it equals core NOTIFY_NAV_LABEL).
 */
function notifyNav(): NavItem { return { to: "/me/notifications", label: "Notifications: what you get, and how", min: "staff", icon: "bell", group: "Your account" }; }
/**
 * The admin Data dashboard link: shown ONLY when the server says this verified sign-in is on DATA_ADMINS (`me.dataAdmin`). It is not part
 * of MORE (which is by role): nobody else, not even an owner or a content admin, sees it. The server gates the page too.
 */
export const DATA_NAV: NavItem = { to: "/admin/data", label: "Data: what we hold for each county", min: "staff", icon: "book", group: "How it's going" };
export const dataLinks = (me: { dataAdmin?: boolean }): NavItem[] => (me.dataAdmin ? [DATA_NAV] : []);
/** Kept for older imports. */
export const NAV = MANAGER_TABS;

export function tabsFor(canManage: boolean): NavItem[] { return canManage ? MANAGER_TABS : STAFF_TABS; }

/** "/settings#level", "/learn#permit": scroll to the section once it has rendered (data loads async, so retry briefly). */
function useHashScroll(path: string, hash: string) {
  useEffect(() => {
    if (!hash || hash.length < 2) return;
    let tries = 0, settled = 0, touched = false;
    const id = decodeURIComponent(hash.slice(1));
    const stop = () => { touched = true; };
    window.addEventListener("wheel", stop, { passive: true }); window.addEventListener("touchstart", stop, { passive: true }); window.addEventListener("keydown", stop);
    // The section can sit below parts that load after it appears (Settings: the page grows as each part loads), so keep
    // putting it at the top for about a second after it is found, unless the person has already scrolled.
    const t = setInterval(() => {
      const el = document.getElementById(id);
      if (el && !touched) { el.scrollIntoView({ block: "start" }); if (++settled > 12) clearInterval(t); }
      else if (touched || ++tries > 20 + 12) clearInterval(t);
    }, 100);
    return () => { clearInterval(t); window.removeEventListener("wheel", stop); window.removeEventListener("touchstart", stop); window.removeEventListener("keydown", stop); };
  }, [path, hash]);
}

const ALL_WORKSPACES = "__all_workspaces__";

/** Typing somewhere: "/" types a slash there, it doesn't open search. */
const typing = (t: EventTarget | null) => t instanceof HTMLElement && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));

/**
 * App search's keys (owner, 2026-10-12): ⌘K / Ctrl+K anywhere, and "/" when not typing. Opens search; focus goes back to where it
 * was when it closes.
 */
export function useAppSearch(): { open: boolean; show: () => void; hide: () => void } {
  const [open, setOpen] = useState(false);
  const back = useRef<HTMLElement | null>(null);
  const show = useCallback(() => { back.current = document.activeElement instanceof HTMLElement ? document.activeElement : null; setOpen(true); }, []);
  const hide = useCallback(() => { setOpen(false); const el = back.current; back.current = null; setTimeout(() => el?.focus?.(), 0); }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "k") { e.preventDefault(); show(); return; }
      if (e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey && !typing(e.target)) { e.preventDefault(); show(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [show]);
  return { open, show, hide };
}

/** "⌘K" on a Mac, "Ctrl K" elsewhere. */
const searchKeyHint = () => (typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent) ? "⌘K" : "Ctrl K");

/** A More item's own "active" look. Plan and billing is its own page (/billing), so the plain route match is right. */
function moreClass(_n: NavItem, _loc: { pathname: string; hash: string }, isActive: boolean): string {
  return isActive ? "active" : "";
}

export function Layout() {
  const s = useSession();
  const nav = useNavigate();
  const auth = useAuth();
  const loc = useLocation();
  const online = useOnline();
  const api = useApi();
  useHashScroll(loc.pathname, loc.hash);
  const search = useAppSearch();
  // "You open often" (app search AS7): this browser counts the screens this person opens here. Never sent.
  useEffect(() => { recordUse(s.me.user.id, s.org.id, loc.pathname); }, [loc.pathname, s.me.user.id, s.org.id]);
  // Back lands where you were (peek and resume §4.5).
  useScrollRestore();
  // Reminders already allowed on this browser: keep the subscription valid for the server's current key (lib/push.ts).
  useEffect(() => {
    if (webPushSupport() !== "granted") return;
    void api.pushSettings().then((p) => refreshWebPush(api, p.config.browser.publicKey)).catch(() => undefined);
  }, [api]);
  const manager = s.can("manager");
  const home = isHomeOrg(s.org);
  const tabs = home ? HOME_TABS : tabsFor(manager);
  // Desktop rail: the tabs minus "More" itself, then the More items directly (no extra click on a big screen).
  const railTabs = tabs.filter((n) => n.to !== "/more");
  const railMore = [...(home ? HOME_MORE : MORE).filter((n) => navShows(s, n)), ...dataLinks(s.me)].filter((n) => !railTabs.some((t) => t.to === n.to));
  return (
    <div className="frame">
      {/* In-app events (docs/APP-EVENTS.md): screens and taps, never a health screen; off unless sharing is on. */}
      <AppEventsBridge />
      <a href="#main" className="skip">Skip to content</a>
      <aside className="rail" aria-label="Main menu">
        <NavLink to="/" className="brand" aria-label="CleanCleaver, Today"><CleaverMark />CleanCleaver</NavLink>
        {isDemoSample(s.org) && <a className="demo-free-top demo-free-rail" href={freeTrialUrl(undefined, "demo-header")} data-testid="demo-rail-free">{HEADER_CTA}</a>}
        <div className="place">
          <span className="place-org">{s.org.name}</span>
          {home ? null : s.locations.length > 1 ? (
            <select aria-label="Location" value={s.location.id} onChange={(e) => s.selectLocation(e.target.value)}>
              {s.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          ) : <span className="caption">{s.location.name}</span>}
          {/* Business picker: switch here, or "All workspaces" for the list and setting up another (docs/WORKSPACES.md). */}
          <select aria-label="Business" value={s.org.id} onChange={(e) => (e.target.value === ALL_WORKSPACES ? nav("/workspaces") : s.selectOrg(e.target.value))}>
            {s.workspaces.map((w) => <option key={w.org.id} value={w.org.id}>{w.home ? `${w.org.name} (home)` : w.org.name}</option>)}
            <option value={ALL_WORKSPACES}>All workspaces…</option>
          </select>
        </div>
        <button type="button" className="search-box" onClick={search.show} aria-label="Search CleanCleaver" aria-keyshortcuts="Meta+K Control+K /" data-testid="search-open">
          <Icon name="search" size={20} /><span>Search</span><kbd>{searchKeyHint()}</kbd>
        </button>
        <nav className="nav nav-tabs" aria-label="Tabs">
          {railTabs.map((n) => <NavLink key={n.to} to={n.to} end={n.to === "/"}><Icon name={n.icon} size={24} />{n.label}</NavLink>)}
        </nav>
        {railMore.length > 0 && (
          <nav className="nav nav-more" aria-label="More">
            <p className="kicker">More</p>
            {moreGrouped(railMore).map((g) => (
              <div key={g.group} role="group" aria-label={g.group || undefined} className="nav-group">
                {g.group ? <p className="kicker nav-group-h" aria-hidden="true">{g.group}</p> : null}
                {g.items.map((n) => <NavLink key={n.to} to={n.to} className={({ isActive }) => moreClass(n, loc, isActive)}><Icon name={n.icon} size={20} />{n.label.split(":")[0]}</NavLink>)}
              </div>
            ))}
          </nav>
        )}
        <div className="rail-foot">
          <span className="rail-who">{s.me.user.name}</span>
          <span className="caption">{home ? "Household" : ROLE_LABEL[s.role]} · {s.me.user.email}</span>
          {config.apiMode === "mock" && <span className="demo-tag" title="Data lives only in this browser"><Icon name="info" size={14} />Demo data</span>}
          <Button small variant="quiet" icon="logout" onClick={() => void auth.signOut()}>{auth.kind === "session" ? "Sign out" : "Switch person"}</Button>
          {isDemoSample(s.org) && <a className="linklike" href={demoSiteUrl()}>Back to cleancleaver.com</a>}
        </div>
      </aside>
      <div className="main-col">
        <header className="topbar">
          <NavLink to="/" className="brand" aria-label="CleanCleaver, Today"><CleaverMark /><span className="brand-word">CleanCleaver</span></NavLink>
          <span className="place-org">{s.location.name}</span>
          {isDemoSample(s.org) && <a className="demo-free-top" href={freeTrialUrl(undefined, "demo-header")} data-testid="demo-header-free">{HEADER_CTA}</a>}
          <button type="button" className="search-icon" onClick={search.show} aria-label="Search CleanCleaver" data-testid="search-open-top"><Icon name="search" size={24} /></button>
        </header>
        {!online && <div className="offline" role="status"><Icon name="wifi" />You're offline. What's on screen may be out of date. Checks and reports you save now wait on this device and send when you're back.</div>}
        <OutboxBanner />
        {/* Plan and billing (docs/PAYMENTS.md): owners/admins only; the card prompt at the value moment, calm banners. */}
        <BillingPrompts />
        {/* A photo or AI call before the plan runs (402 photo_plan): "Start your plan", one tap to Checkout and back. */}
        <PhotoPlanHost />
        {/* "1 entry waiting for your card" (services/api heldentries.ts): mine, with Add a card; saved once the card is in. */}
        <HeldEntries />
        {/* An ownership offer for me (routes/ownership.ts): Accept or Not now, never in the way of a shift. */}
        <OwnershipOfferBanner />
        <main id="main" className="main" tabIndex={-1}>
          {/* One boundary per screen (docs/FAILURES.md): a screen that throws shows a card, never a blank page. */}
          <div key={loc.pathname} className="page-enter"><ScreenBoundary screen={screenName(loc.pathname)}><Suspense fallback={<Skeleton rows={4} label="Opening" />}><Outlet /></Suspense></ScreenBoundary></div>
        </main>
      </div>
      {loc.pathname !== "/check-in" && <AskButton />}
      {/* "Maria's Tacos just signed up with your invite!": once, the next time the referrer opens the app. */}
      {/* Not over the sample café's first card: its friend "Maria's Tacos" is still under Invite a business. */}
      {!isDemoSample(s.org) && <ReferralCelebration />}
      <DemoGate />
      {/* In your hands (peek and resume): one row above the tab bar, empty when nothing is held; the peek a Landed card opens. */}
      {loc.pathname !== "/check-in" && <InYourHands />}
      <PeekHost />
      {search.open ? <AppSearch onClose={search.hide} /> : null}
      <nav className="tabbar" aria-label="Tabs">
        {tabs.map((n) => <NavLink key={n.to} to={n.to} end={n.to === "/"}><Icon name={n.icon} size={26} /><span>{n.label}</span></NavLink>)}
      </nav>
    </div>
  );
}

/** "More" (managers' fifth tab): everything that isn't a tab. */
export function More() {
  const s = useSession();
  const loc = useLocation();
  const auth = useAuth();
  const home = isHomeOrg(s.org);
  const extra: NavItem[] = home ? [...HOME_MORE, ...dataLinks(s.me)] : [...MORE.filter((n) => navShows(s, n)), ...dataLinks(s.me), { to: "/check-in", label: "My daily check-in", min: "staff", icon: "hands", group: "Today" }, { to: "/learn/library", label: "Food safety library", min: "staff", icon: "book", group: "Today" }];
  return (
    <Page title="More" lede={home ? `${s.me.user.name} · ${s.org.name}` : `${s.me.user.name} · ${ROLE_LABEL[s.role]} at ${s.org.name}`}>
      {moreGrouped(extra).map((g) => (
        <section key={g.group} className="more-group" aria-label={g.group || undefined}>
          {g.group ? <h2 className="kicker more-group-h">{g.group}</h2> : null}
          <ul className="more-list">
            {g.items.map((n) => <li key={n.to}><NavLink to={n.to} className={({ isActive }) => moreClass(n, loc, isActive)}><Icon name={n.icon} />{n.label}<Icon name="chevron" size={18} className="push" /></NavLink></li>)}
          </ul>
        </section>
      ))}
      <SimpleStepsCard />
      {/* "Share usage data" in a home's More (a business has it in Settings → More). */}
      {home ? <UsageDataCard /> : null}
      <div className="form-actions"><Button variant="secondary" icon="logout" onClick={() => void auth.signOut()}>{auth.kind === "session" ? "Sign out" : "Switch person"}</Button></div>
    </Page>
  );
}

/**
 * A page behind a switch (core ownership.ts): the role floor, and the owner's switch for this person. Off: who to ask, one way back.
 * Billing also opens for whoever sees the cost (added the card on file, or a location's billing admin).
 */
export function RequireSwitch({ min = "manager", sw, children }: { min?: Role; sw: Switch; children: React.ReactNode }) {
  const s = useSession();
  if (!s.can(min)) return <RequireRole min={min}>{children}</RequireRole>;
  if (navShows(s, { min, sw })) return <>{children}</>;
  const what = sw === "billing" ? "Billing" : sw === "people" ? "People" : "Settings";
  const owner = s.me.memberships.find((m) => m.org.id === s.org.id)?.ownerName ?? "the owner";
  return (
    <Page title={`${what} is off for you`}>
      <div className="blocked" role="alert" data-testid="switch-off">
        <h2><Icon name="x" size={26} />{what} is turned off for you here</h2>
        <p>Ask {owner} to turn it on in People → Permissions.</p>
        <div className="row"><LinkButton to="/" variant="secondary" icon="back">Back to Today</LinkButton></div>
      </div>
    </Page>
  );
}

export function RequireRole({ min, children }: { min: Role; children: React.ReactNode }) {
  const s = useSession();
  if (!s.can(min)) {
    return (
      <Page title="Ask a manager">
        <div className="blocked" role="alert">
          <h2><Icon name="x" size={26} />This page is for {min === "owner" ? "the owner" : min === "admin" ? "owners and admins" : "managers"}</h2>
          <p>{roleBlockedText(s.me.memberships.find((m) => m.org.id === s.org.id)?.ownerName ?? s.me.homes?.find((m) => m.org.id === s.org.id)?.ownerName)}</p>
          <p className="caption">You're signed in as {ROLE_LABEL[s.role].toLowerCase()}. There's no request button yet: tell them what you need.</p>
          <div className="row"><LinkButton to="/" variant="secondary" icon="back">Back to Today</LinkButton></div>
        </div>
      </Page>
    );
  }
  return <>{children}</>;
}
